import { positionNotionalUsd } from '@/features/home/lib/home-exposure'
import {
  getInstrumentUnifiedKind,
  type UnifiedInstrumentKind,
} from '@/features/order/order-form/lib/instrument-filter'
import { resolveOptionInstrument } from '@/features/order/order-form/lib/parse-option-instrument'
import { isOption } from '@/lib/instrument-util'
import type { Account } from '@/types/generated/common/account'
import { OrderSide, type Position } from '@/types/generated/common/trading'
import type {
  Exchange,
  IncrementalTicker,
  Instrument,
  Ticker,
  TradeVenue,
} from '@/types/generated/common/types'

/** What an instrument option card shows. Every market field is optional: it fills in live. */
export interface InstrumentChoiceData {
  symbol: string
  exchange?: Exchange
  /** Undefined until the instrument catalog resolves the symbol. */
  kind?: UnifiedInstrumentKind
  base?: string
  quote?: string
  iconTag?: string
  tickSize?: string
  lastPrice?: string
  /** Percent, e.g. "2.31" for +2.31%. */
  changePercent24h?: string
  volume24h?: string
  /** Per funding interval, as a fraction. Perps only. */
  fundingRate?: string
  option?: OptionChoiceData
}

export interface OptionChoiceData {
  type: 'C' | 'P'
  strike?: number
  /** Expiry, ms since epoch. */
  expiryMs?: number
  markPrice?: string
  /** Fraction or percent, as the venue reports it; see formatIv. */
  markIv?: string
  underlyingPrice?: string
  delta?: string
  gamma?: string
  theta?: string
  vega?: string
}

/** What a position option card shows. */
export interface PositionChoiceData {
  symbol: string
  accountId: string
  accountName?: string
  tradeVenue: TradeVenue
  exchange: Exchange
  side: 'long' | 'short'
  quantity: string
  /** Absolute size in USD. */
  notionalUsd?: number
  entryPrice?: string
  markPrice?: string
  liquidationPrice?: string
  unrealizedPnlUsd?: string
  /** Return on committed capital, percent. */
  pnlPercent?: string
  isOption: boolean
}

const EXPIRY_CODE_MONTHS = 'JANFEBMARAPRMAYJUNJULAUGSEPOCTNOVDEC'

/** "28JUN26" → ms since epoch (08:00 UTC, the crypto-options settlement time). */
function expiryCodeToMs(code: string): number | undefined {
  const match = /^(\d{1,2})([A-Z]{3})(\d{2})$/.exec(code.toUpperCase())
  if (!match) return undefined
  const month = EXPIRY_CODE_MONTHS.indexOf(match[2]) / 3
  if (!Number.isInteger(month)) return undefined
  return Date.UTC(2000 + Number(match[3]), month, Number(match[1]), 8)
}

/**
 * An instrument card's data from what is known about the symbol: its catalog entry (kind,
 * currencies, option terms), the plain ticker (price, 24h change, volume, funding) and, for an
 * option, the incremental ticker (mark, IV, greeks — the plain ticker carries none).
 */
export function buildInstrumentChoiceData(
  symbol: string,
  instrument: Instrument | undefined,
  ticker: Ticker | undefined,
  optionTicker: IncrementalTicker | undefined
): InstrumentChoiceData {
  const kind = instrument ? getInstrumentUnifiedKind(instrument) : undefined
  const isOptionKind = kind === 'option-call' || kind === 'option-put'
  const parsedOption = isOptionKind ? resolveOptionInstrument(symbol, instrument) : null
  const expiryMs =
    Number(instrument?.expirationTsMs) || (parsedOption ? expiryCodeToMs(parsedOption.expiry) : 0)
  return {
    symbol,
    exchange: instrument?.exchange ?? ticker?.exchange,
    kind,
    base: instrument?.baseCurrency || undefined,
    quote: instrument?.quoteCurrency || undefined,
    iconTag: instrument?.enrichment?.iconTag || undefined,
    tickSize: instrument?.tickSize || undefined,
    lastPrice: ticker?.lastPrice ?? ticker?.markPrice ?? optionTicker?.lastPrice,
    changePercent24h: ticker?.priceChangePercent24h,
    volume24h: ticker?.volume24h,
    fundingRate: kind === 'perp' ? (ticker?.fundingRate ?? optionTicker?.fundingRate) : undefined,
    option: isOptionKind
      ? {
          type: kind === 'option-call' ? 'C' : 'P',
          strike: parsedOption?.strike,
          expiryMs: expiryMs || undefined,
          markPrice: optionTicker?.markPrice ?? ticker?.markPrice,
          markIv: optionTicker?.markIv ?? ticker?.iv,
          underlyingPrice: optionTicker?.underlyingPrice ?? ticker?.underlyingPrice,
          delta: optionTicker?.greeks?.delta,
          gamma: optionTicker?.greeks?.gamma,
          theta: optionTicker?.greeks?.theta,
          vega: optionTicker?.greeks?.vega,
        }
      : undefined,
  }
}

/** A position card's data from the live position row and the account it is held on. */
export function buildPositionChoiceData(
  position: Position,
  account: Pick<Account, 'name'> | undefined
): PositionChoiceData {
  const quantity = Number(position.quantity)
  const side =
    position.side === OrderSide.ORDER_SIDE_SELL || (position.side === undefined && quantity < 0)
      ? 'short'
      : 'long'
  const notional = positionNotionalUsd(position)
  return {
    symbol: position.symbol,
    accountId: position.accountId,
    accountName: account?.name,
    tradeVenue: position.tradeVenue,
    exchange: position.exchange,
    side,
    quantity: Number.isFinite(quantity) ? String(Math.abs(quantity)) : position.quantity,
    notionalUsd: notional > 0 ? notional : undefined,
    entryPrice: position.avgPrice || undefined,
    markPrice: position.markPrice,
    liquidationPrice: position.liquidationPrice,
    unrealizedPnlUsd: position.unrealizedPnlUsd ?? position.unrealizedPnl,
    pnlPercent: position.pnlPercent,
    isOption: isOption(position),
  }
}

/**
 * The position a suggested symbol names. The question was asked about one account, so its rows
 * come first; a symbol held only elsewhere still resolves rather than showing nothing.
 */
export function findChoicePosition(
  positions: Position[],
  symbol: string,
  accountId: string | undefined
): Position | undefined {
  const matches = positions.filter(
    (p) => (p.symbol === symbol || p.venueSymbol === symbol) && Number(p.quantity) !== 0
  )
  return matches.find((p) => p.accountId === accountId) ?? matches[0]
}

/** IV as a percent number: venues report it either as a fraction (0.52) or a percent (52). */
export function ivPercent(value: string | undefined): number | undefined {
  const n = Number(value)
  if (!value || !Number.isFinite(n) || n <= 0) return undefined
  return n <= 5 ? n * 100 : n
}
